import { createPasswordRecoveryClient } from '@shared/password-recovery-client';
import { createClient } from '@supabase/supabase-js';
import type { Session } from '@supabase/supabase-js';
// CAPACITOR REMOVED: Mobile app is now handled by billposter-expo

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Platform info - web only now
const isNative = false;
const platform = 'web';

console.log('[AUTH_DEBUG] Platform info:', {
  isNative,
  platform,
  hasSupabaseUrl: !!supabaseUrl,
  hasSupabaseKey: !!supabaseAnonKey,
  supabaseUrlPreview: supabaseUrl ? supabaseUrl.substring(0, 30) + '...' : 'MISSING',
  supabaseKeyPreview: supabaseAnonKey ? supabaseAnonKey.substring(0, 20) + '...' : 'MISSING'
});

// The app client supports existing PKCE links; email landing pages own exchange.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    // Email landing pages own URL exchange, avoiding SDK/effect double consumption.
    detectSessionInUrl: false
  }
});

// This client only sends recovery mail; it never owns an app session.
export function requestPasswordRecovery(email: string, redirectTo: string) {
  return createPasswordRecoveryClient(supabaseUrl, supabaseAnonKey)
    .auth.resetPasswordForEmail(email, { redirectTo });
}

let isInitialized = false;

// CANONICAL session cache - used by all auth flows
// DO NOT create duplicate session caches elsewhere (e.g. in http.ts)
// This is the single source of truth for Supabase session state
let currentSession: Session | null = null;

/**
 * Helper to check if we should log (exclude test environment)
 */
function shouldLog(): boolean {
  try {
    return import.meta.env.MODE !== 'test';
  } catch {
    return true; // Default to logging if env check fails
  }
}

/**
 * Helper to decode JWT token expiration and log token info
 */
function logTokenExpiration(token: string, context: string): void {
  if (!shouldLog()) return;

  try {
    const part = token.split('.')[1] || '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4 || 4)) % 4);
    const json = atob(b64 + pad);
    const payload = JSON.parse(json || '{}');

    if (payload.exp) {
      const expirationDate = new Date(payload.exp * 1000);
      const now = new Date();
      const minutesRemaining = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60));

      console.log(`[AUTH_DEBUG] ${context} - Token expires at ${expirationDate.toISOString()} (${minutesRemaining} minutes remaining)`);
    }
  } catch (error) {
    console.warn('[AUTH_DEBUG] Failed to decode token expiration:', error);
  }
}

/**
 * Initialize Supabase auth and set up session handling
 */
export async function initAuthBootstrap() {
  if (isInitialized) {
    console.log('[AUTH_SUPABASE] Already initialized');
    return { hasSession: !!currentSession };
  }

  try {
    console.log('[AUTH_SUPABASE] Initializing auth bootstrap...');

    // Get current session
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.warn('[AUTH_SUPABASE] Error getting session:', error.message);
    } else if (session) {
      currentSession = session;
      console.log('[AUTH_SUPABASE] Session found on init:', {
        user: session.user?.email,
        hasToken: !!session.access_token
      });

      // Log token expiration for initial session
      if (session.access_token) {
        logTokenExpiration(session.access_token, 'Initial session');
      }

      try {
        // Mirror session to localStorage for explicit persistence/inspection
        localStorage.setItem('supabase.auth.token', JSON.stringify(session));
      } catch { }
      try {
        // One-time claims log for debugging env alignment (no raw token)
        // @ts-ignore
        const g: any = window as any;
        if (!g.__jwtClaimsLogged && session.access_token) {
          g.__jwtClaimsLogged = true;
          const part = session.access_token.split('.')[1] || '';
          const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
          const pad = '='.repeat((4 - (b64.length % 4 || 4)) % 4);
          const json = atob(b64 + pad);
          try {
            const payload = JSON.parse(json || '{}');
            console.log('[JWT_CLAIMS]', { iss: payload?.iss, aud: payload?.aud, sub: payload?.sub, exp: payload?.exp });
            console.log('[JWT_CLAIMS_STR]', `iss=${payload?.iss} aud=${payload?.aud} sub=${payload?.sub} exp=${payload?.exp}`);
          } catch { }
        }
      } catch { }
    } else {
      console.log('[AUTH_SUPABASE] No active session on init');
    }

    // Listen for auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AUTH_SUPABASE] Auth state changed:', { event, hasSession: !!session });
      currentSession = session ?? null;

      if (shouldLog()) {
        // Enhanced logging for specific auth events
        if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          logTokenExpiration(session.access_token, 'Token refreshed successfully');
        } else if (event === 'SIGNED_IN' && session?.access_token) {
          logTokenExpiration(session.access_token, 'User signed in');
        } else if (event === 'SIGNED_OUT') {
          console.log('[AUTH_DEBUG] User signed out, clearing session');
        }
      }

      if (session?.access_token) {
        console.log("[AUTH_SUPABASE] Token refreshed");
        // Update global auth store and localStorage
        try {
          localStorage.setItem('supabase.auth.token', JSON.stringify(session));
        } catch { }
      } else {
        console.log("[AUTH_SUPABASE] Session cleared");

        // Check for refresh failures
        if (shouldLog() && event !== 'SIGNED_OUT' && !session) {
          console.log('[AUTH_DEBUG] Token refresh failed, forcing re-login');
        }

        try {
          localStorage.removeItem('supabase.auth.token');
        } catch { }
      }
    });

    isInitialized = true;
    return { hasSession: !!currentSession };
  } catch (error) {
    console.error('[AUTH_SUPABASE] Error initializing auth:', error);
    isInitialized = true; // Don't retry
    return { hasSession: false };
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1] || '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4 || 4)) % 4);
    const json = atob(b64 + pad);
    return JSON.parse(json || '{}') as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isAccessTokenExpired(token: string, skewSeconds = 30): boolean {
  const payload = decodeJwtPayload(token);
  const exp = typeof payload?.exp === 'number' ? payload.exp : null;
  if (!exp) return false;
  return Date.now() >= (exp - skewSeconds) * 1000;
}

/**
 * Get current Supabase access token
 */
export async function getAccessToken(): Promise<string | null> {
  if (currentSession?.access_token && !isAccessTokenExpired(currentSession.access_token)) {
    if (shouldLog()) {
      logTokenExpiration(currentSession.access_token, 'Token retrieved (cached)');
    }
    return currentSession.access_token;
  }

  if (currentSession?.refresh_token) {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session?.access_token) {
        currentSession = data.session;
        if (shouldLog()) {
          logTokenExpiration(data.session.access_token, 'Token refreshed');
        }
        return data.session.access_token;
      }
      if (error) {
        console.warn('[AUTH_SUPABASE] Token refresh failed:', error.message);
      }
    } catch (error) {
      console.warn('[AUTH_SUPABASE] Token refresh threw:', error);
    }
  }

  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.warn('[AUTH_SUPABASE] Error getting token:', error.message);
      return null;
    }

    if (session?.access_token) {
      currentSession = session;
      console.log('[AUTH_SUPABASE] Token retrieved successfully');

      // Log token expiration when retrieving token
      if (shouldLog()) {
        logTokenExpiration(session.access_token, 'Token retrieved');
      }

      return session.access_token;
    }

    return null;
  } catch (error) {
    console.warn('[AUTH_SUPABASE] Failed to get access token:', error);
    return null;
  }
}

/**
 * Wait for auth to be ready
 */
export async function getAuthReady(): Promise<boolean> {
  if (!isInitialized) {
    await initAuthBootstrap();
  }

  // Check if we have a valid session
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch {
    return false;
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithPassword(email: string, password: string) {
  console.log('[AUTH_SUPABASE] Attempting sign in for:', email);
  console.log('[AUTH_DEBUG] Platform:', platform, 'Native:', isNative);
  console.log('[AUTH_DEBUG] Supabase URL:', supabaseUrl ? 'SET' : 'MISSING');
  console.log('[AUTH_DEBUG] Supabase Key:', supabaseAnonKey ? 'SET' : 'MISSING');

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    console.error('[AUTH_SUPABASE] Sign in failed:', error.message);
    console.error('[AUTH_DEBUG] Full error:', error);
    console.error('[AUTH_DEBUG] Error code:', error.status);
    console.error('[AUTH_DEBUG] Error name:', error.name);
    throw new Error(error.message);
  }

  console.log('[AUTH_SUPABASE] Sign in successful');
  currentSession = data.session;

  // Log token expiration for new sign-in
  if (data.session?.access_token && shouldLog()) {
    logTokenExpiration(data.session.access_token, 'Sign in successful');
  }

  try {
    const token = data.session?.access_token;
    if (token) {
      const part = token.split('.')[1] || '';
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = '='.repeat((4 - (b64.length % 4 || 4)) % 4);
      const json = atob(b64 + pad);
      try {
        const payload = JSON.parse(json || '{}');
        console.log('[JWT_CLAIMS]', { iss: payload?.iss, aud: payload?.aud, sub: payload?.sub, exp: payload?.exp });
        console.log('[JWT_CLAIMS_STR]', `iss=${payload?.iss} aud=${payload?.aud} sub=${payload?.sub} exp=${payload?.exp}`);
      } catch { }
    }
  } catch { }
  return data;
}

/**
 * Sign out
 */
export async function signOut() {
  console.log('[AUTH_SUPABASE] Signing out...');

  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('[AUTH_SUPABASE] Sign out failed:', error.message);
    throw new Error(error.message);
  }

  console.log('[AUTH_SUPABASE] Sign out successful');
  currentSession = null;
}

/**
 * Legacy compatibility - these functions are no longer needed but kept for compatibility
 */
export function setAccessToken(token: string | null) {
  console.warn('[AUTH_SUPABASE] setAccessToken is deprecated - Supabase handles tokens automatically');
}

export function clearAccessToken() {
  console.warn('[AUTH_SUPABASE] clearAccessToken is deprecated - use signOut() instead');
}
